<script setup>
import { injectListStylesIfNeeded } from '@utils/list-styles';
import { SuperDoc } from '@harbour-enterprises/superdoc';
import { defineEmits, defineProps, onMounted, onUnmounted } from 'vue';

const emit = defineEmits(['input']);

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  defaultValue: {
    type: String,
  },
  isSuperdoc: {
    type: Boolean,
    default: true,
  },
  activeSuperdoc: {
    type: File,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

let superdoc = null;
const handleInput = () => {
  const html = getEditorHTML();
  emit('input', html);
};

const handleUpdate = () => {
  const html = getEditorHTML();
  const match = html.match(/<p[^>]*>(.*?)<\/p>/s);
  let result = html;
  // Fallback to the default label if no value set
  if (match[1].length === 1 && !match[1].trim().length) result = '';

  emit('input', result);
}

const getEditorHTML = () => {
  const html = superdoc.getHTML({ unflattenLists: !props.isSuperdoc });
  const tempDocument = document.implementation.createHTMLDocument();
  const container = tempDocument.createElement('div');
  container.insertAdjacentHTML('afterbegin', html);
  processEmptyParagraphs(container);

  // Use utility function to inject list styles if needed
  return injectListStylesIfNeeded(container.innerHTML);
};

const processEmptyParagraphs = (htmlElement) => {
    const pars = [...htmlElement.querySelectorAll('p')];
    pars.forEach((p) => {
        if (p.textContent === '' && !p.childNodes.length) {
            p.textContent = ' ';
        }
    });
};

const addToolbar = () => {
  const config = {
    element: toolbarId,
    mode: 'docx',
    groups: {
      center: [
        'fontSize',
        'bold',
        'italic',
        'underline',
        'color',
        'textAlign',
        'list',
        'numberedlist',
        'link',
      ]
    }
  };
  return config;
};

const containerId = `superdoc-container_${props.id}`;
const toolbarId = `toolbar_${props.id}`;
const initSupereditor = async () => {
  let sourceFile = props.activeSuperdoc;
  let config = {
    selector: `#${containerId}`,
    html: props.defaultValue || '<p></p>',
    suppressDefaultDocxStyles: true,
    disableContextMenu: true,
    modules: {
      toolbar: addToolbar(),
    },
    onEditorUpdate,
    onEditorCreate,
  };
  if (sourceFile) {
    config['document'] = {
      id: `superdoc_${props.id}`,
      type: 'docx',
      data: sourceFile
    };
  }
  superdoc = new SuperDoc(config);
};

const onEditorCreate = () => {
  const html = getEditorHTML();
  const container = document.createElement('div');
  container.innerHTML = html;
  const text = container.textContent.trim();
  const hasContent = text.length > 0;
  if (hasContent) handleInput();
};

const onEditorUpdate = () => {
  handleUpdate();
};

const destroySupereditor = () => {
  superdoc?.destroy();
  superdoc = null;
};

onMounted(() => {
  initSupereditor();
});

onUnmounted(() => {
  destroySupereditor();
});
</script>

<template>
  <div class="superdoc-elements" :style="{opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto'}">
    <div :id="toolbarId"></div>
    <div :id="containerId" class="superdoc-container sd-theme"></div>
  </div>
</template>

<style scoped lang="postcss">
.superdoc-elements {
  height: 100%;
}

.superdoc-elements :deep(.super-editor-container) {
  min-height: 100px;
  min-width: 100%;
  max-height: 211px;
  height: 100%;
  width: 100%;
  overflow: auto;
}

.superdoc-elements :deep(.layers),
.superdoc-elements :deep(.super-editor) {
  width: 100%;
  height: 100%;
  border: none;
}

.superdoc-elements :deep(.ProseMirror) {
  border: none;
  min-height: 100px;
  min-width: 100%;
  max-height: 211px;
  height: 100%;
  width: 100%;
  padding-top: 5px;
}

.superdoc-elements :deep(.superdoc) {
  height: 100%;
  width: 100%;
}

.superdoc-container {
  border: solid 1px #e2e0e0;
  padding: 7px;
  border-radius: 5px;
  transition: all 250ms ease;
}
.superdoc-container:hover {
  border: 1px solid #0160cc86;
  box-shadow:0 0 5px hsla( 0,0%,0%,.1);
}
.superdoc-container:focus-within {
  border: 1px solid #015fcc;
  box-shadow:0 0 5px hsla( 0,0%,0%,.3 );
}
</style>
